import { useEffect, useState } from 'react';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import deLocale from 'date-fns/locale/pt-BR';
import DateFnsUtils from '@date-io/date-fns';
import { TextField } from '@mui/material';
import Popover from '../components/popover/Popover';
import api from 'services/api';
import { useSelector, useDispatch } from 'react-redux';
import { setSnackbar } from 'redux/snackbar';
import BiomeasuresService from 'services/BiomeasuresService';
import { ERROR_MESSAGES, filterMessageError } from './helpers';

export function useBiomeasures() {
  const selectedCycle = useSelector(
    (state) => state.selectedCycle.selectedCycle,
  );
  const [biomeasures, setBiomeasures] = useState([]);
  const [exportData, setExportData] = useState([]);
  const [openProgress, setOpenProgress] = useState(false);
  const [errors, setErrors] = useState([]);
  const [errorsWhenPosting, setErrorsWhenPosting] = useState([]);

  const dispatch = useDispatch();

  const columns = [
    {
      title: 'Viveiro',
      field: 'cycle.pond.description',
      hideFilterIcon: true,
      editable: 'never',
    },
    {
      title: 'Tamanho da Amostra',
      field: 'size',
      hideFilterIcon: true,
      editable: 'never',
    },
    {
      title: 'Data',
      field: 'get_at',
      type: 'date',
      hideFilterIcon: true,
      dateSetting: { locale: 'pt-BR' },
      editComponent: (props) => (
        <LocalizationProvider adapterLocale={deLocale} dateAdapter={DateFnsUtils}>
          <DatePicker
            format="dd/MM/yyyy"
            value={props.value}
            onChange={props.onChange}
            InputProps={{
              style: {
                fontSize: 13,
              },
            }}
            // inputFormat="DD/MM/YYYY"
            renderInput={(params) => <TextField {...params} />}
            fullWidth

          />
        </LocalizationProvider>
      ),
    },
    {
      title: 'Parâmetros',
      field: 'parameters',
      editable: 'never',
      render: (rowData) => {
        let parametersvalue = '';
        rowData.parameters.forEach((parameter) => {
          parametersvalue
            += `${parameter.parameter.name
            }: ${
              parseFloat(parameter.value).toFixed(2)
            } ${
              parameter.parameter.unity.sigla
            }, `;
        });
        return <Popover data={parametersvalue.slice(0, -2)} />;
      },
      hideFilterIcon: true,
    },
  ];

  const columnsForExport = [
    {
      title: 'Ciclo',
      field: 'cycle',
    },
    {
      title: 'Viveiro',
      field: 'pond',
    },
    {
      title: 'Tamanho da Amostra',
      field: 'sample_size',
    },
    {
      title: 'Data',
      field: 'date',
    },
    {
      title: 'Parâmetros',
      field: 'parameter',
    },
    {
      title: 'Valor',
      field: 'value',
    },
  ];

  useEffect(() => {
    async function loadData() {
      try {
        setOpenProgress(false);
        const response = await api.get(
          `/biomeasures/cycle-code/${selectedCycle.id}`,
        );

        if (response.data) {
          const data = await response.data;
          setBiomeasures(data);
          setOpenProgress(true);
        }
      } catch (err) {
        setErrors((prev) => [err.message, ...prev]);
      } finally {
        setOpenProgress(true);
      }
    }
    loadData();
  }, [selectedCycle]);

  useEffect(() => {
    async function loadBiomeasuresExport() {
      try {
        const response = await (await api.get(`/biomeasures/cycle-code/${selectedCycle.id}/export`)).data;

        if (response.length === 0) {
          setOpenProgress(true);
        }
        if (response) {
          setExportData(response);
        }
      } catch (err) {
        setErrors((prev) => [err.message, ...prev]);
        setOpenProgress(true);
      }
    }
    loadBiomeasuresExport();
  }, [selectedCycle]);

  const handleCloseSnackbar = () => {
    setErrorsWhenPosting([]);
  };

  // eslint-disable-next-line consistent-return
  const handleImportDataPost = async (payload) => {
    if (!payload) throw new Error('Payload is required.');
    try {
      setOpenProgress(false);
      const result = await BiomeasuresService.importData(payload);
      const {
        errorsData, importedData, status, successMessage,
      } = result;

      if (status === 201) {
        if (importedData.length === 0) {
          if (errorsData.length > 0) {
            return setErrorsWhenPosting(filterMessageError(errorsData));
          }
          return dispatch(
            setSnackbar(
              true,
              'error',
              ERROR_MESSAGES.EMPTY_DATA,
            ),
          );
        }
        if (importedData.length > 0) {
          const updatedList = await BiomeasuresService.biomeasuresList(selectedCycle.id);
          setBiomeasures(updatedList);
          dispatch(
            setSnackbar(
              true,
              'success',
              successMessage,
            ),
          );
          if (errorsData.length > 0) {
            setErrorsWhenPosting(filterMessageError(errorsData));
          }
          setOpenProgress(true);
        }
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || ERROR_MESSAGES.INSERT_NEW_DATA;
      dispatch(
        setSnackbar(
          true,
          'error',
          errorMessage,
        ),
      );
      throw new Error(errorMessage);
    }
  };

  return {
    biomeasures,
    columns,
    columnsForExport,
    exportData,
    openProgress,
    errors,
    errorsWhenPosting,
    handleCloseSnackbar,
    handleImportDataPost,
  };
}
